/**
 * Live Tracking Page
 * Real-time monitoring of active route executions with stop-by-stop progress
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Loader2, AlertCircle, CheckCircle, Clock, MapPin, Package, Truck,
  RefreshCw, ChevronDown, ChevronUp, Phone, Navigation, Flag
} from 'lucide-react';
import { transportApi, type RouteExecution, type StopLog, type RawMaterialRoute } from '../../services/transport/api';
import LiveStopDetectionMap from './LiveStopDetectionMap';

// Stop status colors
const stopStatusColors: Record<string, { bg: string; text: string; icon: string }> = {
  PENDING: { bg: 'bg-gray-100', text: 'text-gray-600', icon: 'text-gray-400' },
  ARRIVED: { bg: 'bg-blue-100', text: 'text-blue-700', icon: 'text-blue-500' },
  COMPLETED: { bg: 'bg-green-100', text: 'text-green-700', icon: 'text-green-500' },
  SKIPPED: { bg: 'bg-orange-100', text: 'text-orange-700', icon: 'text-orange-500' },
};

// Execution status colors
const executionStatusColors: Record<string, { bg: string; text: string; dot: string }> = {
  SCHEDULED: { bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-500' },
  STARTED: { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
  IN_PROGRESS: { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
  COMPLETED: { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  CANCELLED: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
};

interface ActiveExecution extends RouteExecution {
  stops?: StopLog[];
  canEndRoute?: boolean;
  validationMessage?: string;
  routeDetails?: RawMaterialRoute;
}

function StopCard({ stop, index }: { stop: StopLog; index: number }) {
  const status = stop.status || 'PENDING';
  const statusStyle = stopStatusColors[status] || stopStatusColors.PENDING;

  return (
    <div className={`flex items-start gap-4 p-4 rounded-lg border ${
      status === 'COMPLETED' ? 'border-green-200 bg-green-50' :
      status === 'ARRIVED' ? 'border-blue-200 bg-blue-50' :
      status === 'SKIPPED' ? 'border-orange-200 bg-orange-50' :
      'border-gray-200 bg-white'
    }`}>
      {/* Stop Number */}
      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold ${
        status === 'COMPLETED' ? 'bg-green-500 text-white' :
        status === 'ARRIVED' ? 'bg-blue-500 text-white' :
        status === 'SKIPPED' ? 'bg-orange-500 text-white' :
        'bg-gray-200 text-gray-600'
      }`}>
        {index + 1}
      </div>

      {/* Stop Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h4 className="font-semibold text-gray-900 truncate">
              {stop.gaushalaName || `Gaushala ${stop.gaushalaId}`}
            </h4>
            <p className="text-sm text-gray-500">ID: {stop.gaushalaId}</p>
          </div>
          <div className="flex items-center gap-1.5">
            {stop.partitionLabel && (
              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium bg-blue-600 text-white">
                <Truck className="h-3 w-3" />
                {stop.partitionLabel}
              </span>
            )}
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
              {status === 'COMPLETED' && <CheckCircle className="h-3 w-3" />}
              {status === 'ARRIVED' && <MapPin className="h-3 w-3" />}
              {status === 'SKIPPED' && <AlertCircle className="h-3 w-3" />}
              {status === 'PENDING' && <Clock className="h-3 w-3" />}
              {status}
            </span>
          </div>
        </div>

        {/* Details based on status */}
        <div className="mt-2 space-y-1 text-sm">
          {stop.arrivedAt && (
            <p className="text-gray-600">
              <span className="font-medium">Arrived:</span> {new Date(stop.arrivedAt).toLocaleTimeString()}
            </p>
          )}
          {stop.departedAt && (
            <p className="text-gray-600">
              <span className="font-medium">Departed:</span> {new Date(stop.departedAt).toLocaleTimeString()}
            </p>
          )}
          {stop.quantityCollectedKg && (
            <p className="text-green-700 font-medium">
              <Package className="inline h-4 w-4 mr-1" />
              Collected: {stop.quantityCollectedKg.toFixed(1)} kg
            </p>
          )}
          {stop.skipReason && (
            <p className="text-orange-700">
              <span className="font-medium">Skip Reason:</span> {stop.skipReason}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function ExecutionCard({
  execution,
  isExpanded,
  onToggle,
  onRefresh
}: {
  execution: ActiveExecution;
  isExpanded: boolean;
  onToggle: () => void;
  onRefresh: () => void;
}) {
  const status = execution.statusCode || execution.status || 'SCHEDULED';
  const statusStyle = executionStatusColors[status] || executionStatusColors.SCHEDULED;

  // Calculate progress
  const totalStops = (execution.totalStopsCompleted || 0) + (execution.totalStopsSkipped || 0) +
    (execution.stops?.filter(s => s.status === 'PENDING' || s.status === 'ARRIVED').length || 0);
  const completedStops = execution.totalStopsCompleted || 0;
  const skippedStops = execution.totalStopsSkipped || 0;
  const progressPercent = totalStops > 0 ? Math.round(((completedStops + skippedStops) / totalStops) * 100) : 0;

  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      {/* Header - Click to expand */}
      <button
        onClick={onToggle}
        className="w-full p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            {/* Status indicator */}
            <div className={`flex-shrink-0 w-3 h-3 rounded-full ${statusStyle.dot} ${
              status === 'STARTED' || status === 'IN_PROGRESS' ? 'animate-pulse' : ''
            }`} />

            {/* Route info */}
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-gray-900 truncate">
                {execution.routeName || `Route ${execution.routeCode}`}
              </h3>
              <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                <span className="flex items-center gap-1">
                  <Truck className="h-4 w-4" />
                  {execution.driverName || 'No driver'}
                </span>
                <span className="flex items-center gap-1">
                  <Package className="h-4 w-4" />
                  {execution.vehicleNumber || 'No vehicle'}
                </span>
              </div>
            </div>
          </div>

          {/* Right side - Status & Progress */}
          <div className="flex items-center gap-4">
            {/* Progress */}
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900">{progressPercent}%</p>
              <p className="text-xs text-gray-500">{completedStops + skippedStops}/{totalStops} stops</p>
            </div>

            {/* Status badge */}
            <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
              {execution.statusName || status}
            </span>

            {/* Expand icon */}
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-3 h-2 w-full rounded-full bg-gray-200 overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${
              status === 'COMPLETED' ? 'bg-green-500' : 'bg-blue-500'
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-gray-200 p-4">
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="bg-green-50 rounded-lg p-3">
              <p className="text-xs text-green-600 font-medium">Collected</p>
              <p className="text-lg font-bold text-green-700">
                {execution.totalQuantityCollectedKg?.toFixed(1) || '0'} kg
              </p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-xs text-blue-600 font-medium">Completed</p>
              <p className="text-lg font-bold text-blue-700">{completedStops} stops</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-3">
              <p className="text-xs text-orange-600 font-medium">Skipped</p>
              <p className="text-lg font-bold text-orange-700">{skippedStops} stops</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-600 font-medium">Distance</p>
              <p className="text-lg font-bold text-gray-700">
                {execution.actualDistanceKm?.toFixed(1) || '-'} km
              </p>
            </div>
          </div>

          {/* Live Stop Detection Map */}
          {execution.stops && execution.stops.length > 0 && (
            <LiveStopDetectionMap
              stops={execution.stops}
              mfgUnitLatitude={execution.routeDetails?.mfgUnitLatitude}
              mfgUnitLongitude={execution.routeDetails?.mfgUnitLongitude}
              mfgUnitName={execution.routeDetails?.mfgUnitName}
            />
          )}

          {/* Can End Route indicator */}
          {execution.canEndRoute !== undefined && (
            <div className={`mb-4 p-3 rounded-lg ${
              execution.canEndRoute ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
            }`}>
              <div className="flex items-center gap-2">
                {execution.canEndRoute ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-green-700 font-medium">All stops completed - Route can be ended</span>
                  </>
                ) : (
                  <>
                    <Flag className="h-5 w-5 text-yellow-600" />
                    <span className="text-yellow-700 font-medium">{execution.validationMessage}</span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Stops list */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-gray-900">Stop Progress</h4>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRefresh();
                }}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>

            {execution.stops && execution.stops.length > 0 ? (
              <div className="space-y-3">
                {execution.stops
                  .sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0))
                  .map((stop, index) => (
                    <StopCard key={stop.id} stop={stop} index={index} />
                  ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">Loading stops...</p>
            )}
          </div>

          {/* Timestamps */}
          <div className="text-xs text-gray-500 space-y-1 border-t border-gray-200 pt-3">
            {execution.startedAt && (
              <p>Started: {new Date(execution.startedAt).toLocaleString()}</p>
            )}
            {execution.completedAt && (
              <p>Completed: {new Date(execution.completedAt).toLocaleString()}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function LiveTrackingPage() {
  const [executions, setExecutions] = useState<ActiveExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Fetch active executions
  const fetchExecutions = useCallback(async () => {
    try {
      setError(null);

      // Get all executions
      const allExecutions = await transportApi.getExecutions(true);

      // Filter to only active (STARTED, IN_PROGRESS) and today's SCHEDULED
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const activeExecutions = allExecutions.filter(e => {
        const status = e.statusCode || e.status;
        const execDate = new Date(e.executionDate);
        execDate.setHours(0, 0, 0, 0);

        // Include STARTED and IN_PROGRESS
        if (status === 'STARTED' || status === 'IN_PROGRESS') return true;

        // Include today's SCHEDULED
        if (status === 'SCHEDULED' && execDate.getTime() === today.getTime()) return true;

        return false;
      });

      // Fetch stops and route details for expanded executions
      const executionsWithStops = await Promise.all(
        activeExecutions.map(async (exec) => {
          if (expandedIds.has(exec.id!) || exec.statusCode === 'STARTED' || exec.statusCode === 'IN_PROGRESS') {
            try {
              const [stops, validation, routeDetails] = await Promise.all([
                transportApi.getExecutionStops(exec.id!),
                transportApi.validateCanEndRoute(exec.id!),
                transportApi.getRouteDetails(exec.routeId).catch(() => undefined),
              ]);
              return {
                ...exec,
                stops,
                canEndRoute: validation.canEndRoute,
                validationMessage: validation.message,
                routeDetails,
              };
            } catch {
              return { ...exec, stops: [] };
            }
          }
          return exec;
        })
      );

      setExecutions(executionsWithStops);
    } catch (err) {
      console.error('Failed to fetch executions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load executions');
    } finally {
      setLoading(false);
    }
  }, [expandedIds]);

  // Initial load
  useEffect(() => {
    fetchExecutions();
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchExecutions();
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, fetchExecutions]);

  // Toggle expansion
  const toggleExpand = (id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Refresh single execution
  const refreshExecution = async (executionId: number) => {
    try {
      const execution = executions.find(e => e.id === executionId);
      const [stops, validation, routeDetails] = await Promise.all([
        transportApi.getExecutionStops(executionId),
        transportApi.validateCanEndRoute(executionId),
        execution?.routeId
          ? transportApi.getRouteDetails(execution.routeId).catch(() => undefined)
          : Promise.resolve(undefined),
      ]);

      setExecutions(prev =>
        prev.map(exec =>
          exec.id === executionId
            ? { ...exec, stops, canEndRoute: validation.canEndRoute, validationMessage: validation.message, routeDetails: routeDetails || exec.routeDetails }
            : exec
        )
      );
    } catch (err) {
      console.error('Failed to refresh execution:', err);
    }
  };

  // Count by status
  const startedCount = executions.filter(e => (e.statusCode || e.status) === 'STARTED' || (e.statusCode || e.status) === 'IN_PROGRESS').length;
  const scheduledCount = executions.filter(e => (e.statusCode || e.status) === 'SCHEDULED').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Live Route Tracking</h2>
          <p className="text-sm text-gray-500 mt-1">
            Monitor active route executions and stop-by-stop progress
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Auto-refresh toggle */}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Auto-refresh (30s)
          </label>

          {/* Manual refresh */}
          <button
            onClick={() => {
              setLoading(true);
              fetchExecutions();
            }}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex gap-3">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
              <Truck className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-blue-600">In Progress</p>
              <p className="text-2xl font-bold text-blue-700">{startedCount}</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-gray-500 flex items-center justify-center">
              <Clock className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Scheduled Today</p>
              <p className="text-2xl font-bold text-gray-700">{scheduledCount}</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-green-500 flex items-center justify-center">
              <Package className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-green-600">Total Active</p>
              <p className="text-2xl font-bold text-green-700">{executions.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Executions list */}
      {loading && executions.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : executions.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
          <Truck className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-600 mb-2">No active routes</p>
          <p className="text-sm text-gray-500">
            Active route executions will appear here when drivers start their routes.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* In Progress first */}
          {executions
            .filter(e => (e.statusCode || e.status) === 'STARTED' || (e.statusCode || e.status) === 'IN_PROGRESS')
            .map((execution) => (
              <ExecutionCard
                key={execution.id}
                execution={execution}
                isExpanded={expandedIds.has(execution.id!) || true}
                onToggle={() => toggleExpand(execution.id!)}
                onRefresh={() => refreshExecution(execution.id!)}
              />
            ))}

          {/* Then Scheduled */}
          {executions
            .filter(e => (e.statusCode || e.status) === 'SCHEDULED')
            .map((execution) => (
              <ExecutionCard
                key={execution.id}
                execution={execution}
                isExpanded={expandedIds.has(execution.id!)}
                onToggle={() => toggleExpand(execution.id!)}
                onRefresh={() => refreshExecution(execution.id!)}
              />
            ))}
        </div>
      )}

      {/* Info box */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <h3 className="font-semibold text-blue-900 mb-2">Stop Status Guide</h3>
        <div className="grid gap-2 text-sm text-blue-800 sm:grid-cols-2">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-gray-400"></span>
            <span><strong>PENDING:</strong> Driver hasn't reached this stop yet</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-blue-500"></span>
            <span><strong>ARRIVED:</strong> Driver is at this gaushala</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-green-500"></span>
            <span><strong>COMPLETED:</strong> Material collected successfully</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-orange-500"></span>
            <span><strong>SKIPPED:</strong> Stop was skipped (no material, closed, etc.)</span>
          </div>
        </div>
      </div>
    </div>
  );
}
